import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactECharts from 'echarts-for-react';
import { WidgetDataResult, WidgetChartConfig, CardGridMetricDef } from '../types';
import { formatDateCategory, formatNumber, escapeHtml } from './echartsUtils';
import { mapThreatsSubjects, mapThreatsTypesConst } from '../../../utils/threats';
import { useCurrentUser } from '../../../contexts/CurrentUser';
import { useDashboard } from '../contexts/DashboardContext';
import { ampli } from '../../../ampli';
import { SENTIMENT_COLORS, normalizeSentiment, SentimentValue } from './sentimentColors';
import styles from './ChartCardGrid.module.scss';

const DEFAULT_CARDS_PER_PAGE = 9;

function formatCompact(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return n.toLocaleString();
}

function formatMetricValue(raw: unknown, format?: CardGridMetricDef['format']): string {
  if (raw === null || raw === undefined) return '—';
  const num = Number(raw);
  if (!isNaN(num) && format !== 'number') return formatCompact(num);
  return String(raw);
}

function parseBadges(raw: unknown): string[] {
  if (raw === null || raw === undefined || raw === '') return [];
  if (Array.isArray(raw)) return raw.map(String).filter(Boolean);
  const str = String(raw).trim();
  if (str.startsWith('[')) {
    try {
      const parsed = JSON.parse(str);
      if (Array.isArray(parsed)) return parsed.map(String).filter(Boolean);
    } catch {}
  }
  return str.split(',').map((s) => s.trim()).filter(Boolean);
}

interface SparklinePoint { date?: string; cnt: number; }

function parseSparkline(raw: string | number | boolean | null | unknown): { values: number[]; dates: string[] } {
  const empty = { values: [], dates: [] };
  if (raw === null || raw === undefined) return empty;

  const arr: unknown[] = Array.isArray(raw)
    ? (raw as unknown[])
    : (() => { try { const p = JSON.parse(String(raw)); return Array.isArray(p) ? p : []; } catch { return []; } })();

  if (arr.length === 0) return empty;

  // Object format: [{ date, cnt }, ...]
  if (typeof arr[0] === 'object' && arr[0] !== null && 'cnt' in (arr[0] as object)) {
    const points = arr as SparklinePoint[];
    return {
      values: points.map((p) => Number(p.cnt)).filter((v) => !isNaN(v)),
      dates:  points.map((p) => p.date ?? ''),
    };
  }

  // Flat number array fallback
  return { values: arr.map(Number).filter((v) => !isNaN(v)), dates: [] };
}

interface SparklineSVGProps {
  values: number[];
  dates?: string[];
  color: string;
  label?: string;
}

function SparklineSVG({ values, dates, color, label }: SparklineSVGProps) {
  if (values.length < 2) return null;

  const option = {
    animation: false,
    tooltip: {
      trigger: 'axis' as const,
      appendToBody: true,
      axisPointer: { type: 'line' as const },
      formatter: (params: { dataIndex: number; value: number }[]) => {
        const { dataIndex, value } = params[0];
        const date = dates?.[dataIndex];
        return [
          label ? `<span style="font-size:11px;color:#888">${escapeHtml(label)}</span>` : '',
          date  ? `<span style="font-size:11px;color:#888">${escapeHtml(date)}</span>` : '',
          `<b>${formatNumber(value)}</b>`,
        ].filter(Boolean).join('<br/>');
      },
    },
    grid: { left: 0, right: 0, top: 4, bottom: 4 },
    xAxis: { type: 'category' as const, show: false, boundaryGap: false },
    yAxis: { type: 'value' as const, show: false },
    series: [
      {
        type: 'line' as const,
        data: values,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        showSymbol: false,
        lineStyle: { color, width: 2 },
        areaStyle: {
          color: {
            type: 'linear' as const,
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: `${color}59` },
              { offset: 1, color: `${color}00` },
            ],
          },
        },
      },
    ],
  };

  return (
    <ReactECharts
      option={option}
      style={{ width: '100%', height: '100%' }}
      opts={{ renderer: 'svg' }}
    />
  );
}

interface ChartCardGridProps {
  data: WidgetDataResult;
  config: WidgetChartConfig;
}

export const ChartCardGrid: React.FC<ChartCardGridProps> = ({ data, config }) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [currentUser] = useCurrentUser();
  const { displayedDashboard } = useDashboard();
  const cfg = config.cardGridConfig ?? {};
  const sparklineColor = cfg.sparklineColor ?? '#3B83F6';
  const sparklineLabel = cfg.sparklineLabel;
  const metrics = cfg.metrics ?? [];
  const showDescription = cfg.showDescription !== false;
  const showMetrics = cfg.showMetrics !== false;
  const clickUrlTemplate = cfg.clickUrlTemplate;
  const clickTarget = cfg.clickTarget ?? '_blank';
  const topicsCol = cfg.topicsCol;
  const tagsCol = cfg.tagsCol;
  const showBadges = cfg.showBadges !== false;
  const sentimentCol = cfg.sentimentCol;
  const cardsPerPage = Number.isInteger(cfg.cardsPerPage) && cfg.cardsPerPage! > 0
    ? cfg.cardsPerPage!
    : DEFAULT_CARDS_PER_PAGE;
  const sentimentBackground = cfg.sentimentBackground === true;
  const cardBackground = cfg.cardBackground;
  const cardBackgroundCol = cfg.cardBackgroundCol;

  const sentimentLabels: Record<SentimentValue, string> = {
    positive:        t('Positive'),
    negative:        t('Negative'),
    neutral:         t('Neutral'),
    mostly_positive: t('Mostly positive'),
    mostly_negative: t('Mostly negative'),
    controversial:   t('Controversial'),
  };

  useEffect(() => { setPage(0); }, [data.rows, cardsPerPage]);

  const { totalPages, pageRows } = useMemo(() => {
    const total = Math.ceil(data.rows.length / cardsPerPage);
    const rows = data.rows.slice(page * cardsPerPage, (page + 1) * cardsPerPage);
    return { totalPages: total, pageRows: rows };
  }, [data.rows, page, cardsPerPage]);

  // Auto-detect columns if not explicitly configured
  const dateCol = cfg.dateCol ?? data.columns.find((c) => /date|time|day/i.test(c));
  const dateEndCol = cfg.dateEndCol;
  const titleCol = cfg.titleCol
    ?? data.columns.find((c) => /title|name|label|heading|subject/i.test(c))
    ?? data.columns.find((c) => {
      const v = data.rows[0]?.[c];
      return typeof v === 'string' && v.length > 20;
    })
    ?? data.columns[0];
  const descCol = cfg.descriptionCol ?? data.columns.find((c) => /desc|text|summary|body/i.test(c));
  const sparklineCol = cfg.sparklineCol
    ?? data.columns.find((c) => {
      const v = data.rows[0]?.[c];
      return (typeof v === 'string' && v.trimStart().startsWith('[')) || Array.isArray(v);
    });

  const buildUrl = (row: Record<string, unknown>): string | null => {
    if (!clickUrlTemplate) return null;
    const isExternalMode = /^\{\w+\}$/.test(clickUrlTemplate.trim());
    return clickUrlTemplate.replace(/\{(\w+)\}/g, (_, col) => {
      if (row[col] == null) return '';
      const val = String(row[col]);
      return isExternalMode && /^https?:\/\//i.test(val) ? val : encodeURIComponent(val);
    });
  };

  const handleCardClick = useCallback((cardUrl: string) => {
    ampli.track({
      event_type: 'Widget click action',
      event_properties: {
        user_id: currentUser?.id,
        workspace_id: currentUser?.workspace_id,
        dashboard_id: displayedDashboard?.id,
        widget_type: 'card_grid',
        action_type: 'url',
        is_external: /^https?:\/\//i.test(cardUrl),
      },
    });
    window.open(cardUrl, clickTarget);
  }, [currentUser, displayedDashboard?.id, clickTarget]);

  const handlePrev = () => setPage((p) => Math.max(0, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages - 1, p + 1));

  return (
    <div className={styles.root}>
      <div className={styles.grid} role="list">
        {pageRows.map((row, i) => {
          const dateStart = dateCol ? formatDateCategory(String(row[dateCol] ?? '')) : '';
          const dateEnd = dateEndCol ? formatDateCategory(String(row[dateEndCol] ?? '')) : '';
          const dateRaw = dateStart && dateEnd && dateEnd !== dateStart
            ? `${dateStart} — ${dateEnd}`
            : dateStart;
          const title = titleCol ? String(row[titleCol] ?? '') : '';
          const description = descCol ? String(row[descCol] ?? '') : '';
          const { values: sparklineValues, dates: sparklineDates } = sparklineCol
            ? parseSparkline(row[sparklineCol])
            : { values: [], dates: [] };
          const hasSparkline = sparklineValues.length >= 2;
          const topicBadges = topicsCol
            ? parseBadges(row[topicsCol]).map((k) => (mapThreatsSubjects as Record<string, string>)[k] ?? k)
            : [];
          const tagBadges = tagsCol
            ? parseBadges(row[tagsCol]).map((k) => (mapThreatsTypesConst as Record<string, string>)[k] ?? k)
            : [];
          const hasBadges = showBadges && (topicBadges.length > 0 || tagBadges.length > 0);
          const sentiment = sentimentCol ? normalizeSentiment(row[sentimentCol]) : null;
          const rowBackground = cardBackgroundCol ? String(row[cardBackgroundCol] ?? '').trim() : '';
          const cardBg = rowBackground || cardBackground;

          const cardUrl = buildUrl(row);

          return (
            <article
              key={page * cardsPerPage + i}
              className={`${styles.card} ${cardUrl ? styles.cardClickable : ''}`}
              role="listitem"
              onClick={cardUrl ? () => handleCardClick(cardUrl) : undefined}
              onKeyDown={cardUrl ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(cardUrl); } } : undefined}
              tabIndex={cardUrl ? 0 : undefined}
              aria-label={cardUrl && title ? `Open ${title}` : undefined}
              data-sentiment={sentimentBackground && sentiment ? sentiment : undefined}
              style={cardBg ? ({ '--cg-card-bg': cardBg } as React.CSSProperties) : undefined}
            >
              {dateRaw && (
                <time className={styles.date}>{dateRaw}</time>
              )}
              <div className={styles.cardBody}>
                <div className={styles.titleBlock}>
                  {title && <h3 className={styles.title}>{title}</h3>}
                </div>
                {hasSparkline && (
                  <div className={styles.sparklineWrap}>
                    <SparklineSVG
                      values={sparklineValues}
                      dates={sparklineDates}
                      color={sparklineColor}
                      label={sparklineLabel ?? (sparklineCol
                        ? sparklineCol.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                        : undefined)}
                    />
                  </div>
                )}
              </div>
              {showDescription && description && (
                <p className={styles.description}>{description}</p>
              )}
              {((showMetrics && metrics.length > 0) || hasBadges || sentiment) && (
                <div className={styles.cardFooter}>
                  {((showMetrics && metrics.length > 0) || sentiment) && (
                    <dl className={styles.metrics}>
                      {showMetrics && metrics.map((m) => (
                        <div key={m.col} className={styles.metricItem}>
                          <dt className={styles.metricLabel}>{m.label}</dt>
                          <dd className={styles.metricValue}>{formatMetricValue(row[m.col], m.format)}</dd>
                        </div>
                      ))}
                      {sentiment && (
                        <div className={styles.metricItem}>
                          <dt className={styles.metricLabel}>{t('Sentiment')}</dt>
                          <dd className={styles.metricValue} style={{ color: SENTIMENT_COLORS[sentiment].text }}>
                            {sentimentLabels[sentiment]}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                  {hasBadges && (
                    <ul className={styles.badges} aria-label="Topics and tags">
                      {topicBadges.map((badge) => (
                        <li key={`topic-${badge}`} className={`${styles.badge} ${styles.badgeTopic}`}>{badge}</li>
                      ))}
                      {tagBadges.map((badge) => (
                        <li key={`tag-${badge}`} className={`${styles.badge} ${styles.badgeTag}`}>{badge}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </article>
          );
        })}
      </div>

      {totalPages > 1 && (
        <nav className={styles.pagination} aria-label="Card grid pagination">
          <button
            type="button"
            className={styles.pageBtn}
            onClick={handlePrev}
            disabled={page === 0}
            aria-label="Previous page"
          >
            ‹
          </button>
          <span className={styles.pageNum} aria-live="polite" aria-atomic="true">
            {page + 1} / {totalPages}
          </span>
          <button
            type="button"
            className={styles.pageBtn}
            onClick={handleNext}
            disabled={page === totalPages - 1}
            aria-label="Next page"
          >
            ›
          </button>
        </nav>
      )}
    </div>
  );
};
